import { useId, useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { useSiteConfig } from "../config/SiteConfigContext";
import { useCmsText } from "../hooks/useCmsText";
import { useIsMobile } from "../hooks/useIsMobile";
import { useMediaQuery } from "../hooks/useMediaQuery";
import type { StepId } from "./ids";
import { HEADER_OFFSET, rootStyle } from "./tokens";
import type { ComposerApi } from "./useRequestComposer";
import { ContactStep } from "./parts/ContactStep";
import { DetailsStep } from "./parts/DetailsStep";
import { FinalBlock } from "./parts/FinalBlock";
import { Glyph } from "./parts/Icons";
import { Postcard, PostcardMini } from "./parts/Postcard";
import { StepIndicator } from "./parts/StepIndicator";
import { SuccessView } from "./parts/SuccessView";
import { TopicStep } from "./parts/TopicStep";
import "./richiesta.css";

gsap.registerPlugin(useGSAP);

const motionOK = () => {
  try {
    return window.matchMedia("(prefers-reduced-motion: no-preference)").matches;
  } catch {
    return false;
  }
};

interface Props {
  api: ComposerApi;
  variant: "page" | "panel";
  /** Nel pannello il titolo sta nell'intestazione del dialog: il form lo usa come nome. */
  titleId?: string;
}

/**
 * «Inviaci una richiesta», la vista condivisa da pagina Contatti (`page`) e
 * widget (`panel`). Un solo `<form noValidate>`: tutti i passi restano montati
 * (nascosti con `hidden`), quindi informativa, consenso E e pulsante d'invio
 * esistono sempre nel modulo e l'ordine del DOM resta quello che i test e i
 * lettori di schermo si aspettano.
 *
 * Sopra i 1180 px la pagina affianca la cartolina viva ai passi; sotto, una
 * mini-cartolina accompagna i passi e la cartolina intera compare al passo finale.
 */
export function RequestComposer({ api, variant, titleId }: Props) {
  const cfg = useSiteConfig();
  const ct = useCmsText("contact");
  const isMobile = useIsMobile();
  const wideViewport = useMediaQuery("(min-width: 1180px)");
  const ownTitleId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const prevStep = useRef<StepId | null>(null);
  const { state, t } = api;
  const step = state.step;
  const wide = variant === "page" && wideViewport;

  // Cambio di passo: il nuovo entra di lato (0,32 s). Mai al primo disegno né con movimento ridotto.
  useGSAP(() => {
    const prev = prevStep.current;
    prevStep.current = step;
    if (prev === null || prev === step || !motionOK()) return;
    const dir = step > prev ? 1 : -1;
    gsap.fromTo(
      `[data-step="${step}"]`,
      { x: 16 * dir, opacity: 0 },
      { x: 0, opacity: 1, duration: 0.32, ease: "expo.out", clearProps: "transform,opacity" },
    );
  }, { scope: rootRef, dependencies: [step], revertOnUpdate: true });

  // Un solo annuncio per volta: invio, esito, oppure la quantità portata al minimo.
  const status = state.phase === "sending"
    ? t("contact.guide.sending")
    : state.phase === "success"
      ? ct("sentTitle")
      : state.notice
        ? t("contact.guide.qty.raised", { n: state.notice.qtyRaised })
        : "";
  const miniTop = variant === "panel" ? 0 : isMobile ? HEADER_OFFSET.mobile : HEADER_OFFSET.desktop;
  const d = state.draft;

  const postcard = (
    <Postcard
      t={t}
      free={d.intent === "other"}
      message={api.composed.message}
      headline={api.composed.headline}
      name={d.name}
      company={d.company}
      email={api.emailValue}
      phone={d.phone}
      stamps={api.stamps}
    />
  );

  const layout = (
    <div className={`rq-layout ${wide ? "rq-layout--wide" : "rq-layout--narrow"}`}>
      <div className="rq-main">
        {variant === "page" && <StepIndicator api={api} />}
        {state.pendingCart && <PendingCart api={api} />}
        {!wide && step !== 3 && (
          <PostcardMini t={t} headline={api.composed.headline} note={d.note} stampsCount={api.stamps.length} top={miniTop} />
        )}
        <TopicStep api={api} />
        <DetailsStep api={api} />
        <ContactStep api={api} />
        {variant === "page" && step !== 3 && <StepNav api={api} />}
      </div>
      <div className="rq-side" hidden={!wide && step !== 3}>
        {postcard}
        <FinalBlock api={api} />
      </div>
    </div>
  );

  return (
    <div ref={rootRef} className={`rq-root rq-root--${variant}`} style={rootStyle(cfg)}>
      <div className="rq-sr" role="status" data-gb="stato">{status}</div>
      {variant === "page" && (
        <header className="rq-head">
          <h2 id={ownTitleId} className="rq-head__title" tabIndex={-1} data-rq-focus-target data-gb="contact-title">
            {ct("formTitle")}
          </h2>
          <p className="rq-head__intro">{t("contact.guide.intro")}</p>
        </header>
      )}
      {state.phase === "success" && state.snapshot ? (
        variant === "panel" ? (
          <div className="rq-body" ref={api.setEl("body")}><SuccessView api={api} variant={variant} wide={false} /></div>
        ) : (
          <SuccessView api={api} variant={variant} wide={wide} />
        )
      ) : (
        <form
          ref={api.setEl("form")}
          noValidate
          aria-labelledby={titleId ?? ownTitleId}
          aria-busy={api.sending || undefined}
          className={variant === "panel" ? "rq-form rq-form--panel" : "rq-form"}
          onKeyDown={api.onFormKeyDown}
          onSubmit={api.onSubmit}
          data-gb="richiesta"
        >
          {variant === "panel" ? (
            <>
              <div className="rq-body" ref={api.setEl("body")}>{layout}</div>
              {step !== 3 && <StepNav api={api} bar />}
            </>
          ) : (
            layout
          )}
        </form>
      )}
    </div>
  );
}

/** «Indietro» e «Continua» dei passi 1–2: in fondo al passo (pagina) o nella barra fissa (pannello). */
function StepNav({ api, bar = false }: { api: ComposerApi; bar?: boolean }) {
  const { t, state } = api;
  const first = state.step === 1;
  const primary = state.editReturn ? t("contact.guide.nav.backToSummary") : t("contact.guide.nav.continue");
  return (
    <div className={bar ? `rq-bar${first ? " rq-bar--single" : ""}` : `rq-nav${first ? " rq-nav--end" : ""}`}>
      {/* Un doppio clic non deve saltare un passo: conta solo il primo (da tastiera detail vale 0). */}
      {!first && (
        <button type="button" className="rq-btn rq-btn--ghost" onClick={(e) => { if (e.detail <= 1) api.back(); }} data-gb="indietro">
          <Glyph name="prev" size={14} />
          {t("contact.guide.nav.back")}
        </button>
      )}
      <button type="button" className="rq-btn rq-btn--primary" onClick={(e) => { if (e.detail <= 1) api.next(); }} data-gb="avanti">
        {primary}
        <Glyph name="next" size={14} className="rq-btn__arrow" />
      </button>
    </div>
  );
}

/**
 * Contesto del carrello arrivato su una richiesta già iniziata: la si tiene.
 * Unire le confezioni ha senso solo per gli argomenti che le usano.
 */
function PendingCart({ api }: { api: ComposerApi }) {
  const { t, state } = api;
  const intent = state.draft.intent;
  const canMerge = !intent || intent === "offer" || intent === "custom" || intent === "branding";
  return (
    <div ref={api.setEl("pending")} tabIndex={-1} data-rq-focus-target className="rq-callout rq-pending" data-gb="carrello-in-sospeso">
      <Glyph name="info" size={16} />
      <div className="rq-callout__body">
        <span>{t("contact.guide.hints.cartPending")}</span>
        <span className="rq-pending__actions">
          {canMerge && (
            <button type="button" className="rq-link" onClick={api.mergeCart} data-gb="unisci-carrello">
              {t("contact.guide.hints.cartMerge")}
            </button>
          )}
          <button type="button" className="rq-link" onClick={api.dismissCart} data-gb="lascia-cosi">
            {t("contact.guide.hints.cartIgnore")}
          </button>
        </span>
      </div>
    </div>
  );
}
